import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  Avatar,
  AvatarDepartmentAggregate,
  AvatarStub,
  MemberRoleProject,
  MemberRoleProjectRequest,
  ProjectMemberRole,
} from '../models/avatar-model';
import { API_ENDPOINTS } from '../common/api/api-endpoints';

/**
 * AvatarService
 *
 * HTTP-Service für die Backend-Endpunkte unter /user.
 * "Avatar" entspricht dem Backend-Konzept eines Entra-ID-Users.
 * Die `{oid}` ist die Object-ID aus dem Entra ID Token.
 *
 * Hinweis: POST /user (createAvatar) wird vom Frontend nicht aufgerufen –
 * Avatare werden serverseitig via Entra ID angelegt.
 */
@Injectable({
  providedIn: 'root',
})
export class AvatarService {
  private readonly http = inject(HttpClient);

  /** GET /user/{oid} → Avatar */
  getAvatarByOid(oid: string): Observable<AvatarDepartmentAggregate> {
    return this.http.get<AvatarDepartmentAggregate>(`${API_ENDPOINTS.USER}/${oid}`);
  }

  /**
   * POST /user → Avatar (201)
   * Legt den aktuell authentifizierten User im Backend an oder aktualisiert ihn (Touch).
   * Kein Body nötig im normalen Login-Flow – Backend liest OID/DisplayName/Email aus dem JWT-Token.
   * Optional: AvatarStub für Admin-Flow (fremden User anlegen).
   */
  touchOrCreate(avatarStub?: AvatarStub): Observable<Avatar> {
    return this.http.post<Avatar>(API_ENDPOINTS.USER, avatarStub ?? null);
  }

  /** GET /user/{oid}/member → MemberRoleProject[] (user-zentriert) */
  getMembersByOid(oid: string): Observable<MemberRoleProject[]> {
    return this.http.get<MemberRoleProject[]>(`${API_ENDPOINTS.USER}/${oid}/member`);
  }

  /** POST /user/{oid}/member → MemberRoleProject (201) */
  addMember(oid: string, payload: MemberRoleProjectRequest): Observable<MemberRoleProject> {
    return this.http.post<MemberRoleProject>(`${API_ENDPOINTS.USER}/${oid}/member`, payload);
  }

  /** GET /user/{oid}/member/{memberId} → MemberRoleProject */
  getMemberById(oid: string, memberId: number): Observable<MemberRoleProject> {
    return this.http.get<MemberRoleProject>(`${API_ENDPOINTS.USER}/${oid}/member/${memberId}`);
  }

  /** GET /project/{projectCode}/member → ProjectMemberRole[] (projekt-zentriert) */
  getProjectMembers(projectCode: string): Observable<ProjectMemberRole[]> {
    return this.http.get<ProjectMemberRole[]>(`${API_ENDPOINTS.PROJECT}/${projectCode}/member`);
  }
}

